import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, MailIcon, StarIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, ArrowRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import BackButton from '../UI/BackButton';
// --- Types ---
export interface ProfileData {
  id: string;
  name: string;
  role: string;
  avatar: string;
  coverImage: string;
  status: string;
  queueStatus: 'open' | 'waitlist' | 'closed';
  slotsAvailable: number;
  maxSlots: number;
  estimatedWait: string;
  rating: number;
  location: string;
  bio: string;
  specialties: string[];
  stats: {
    label: string;
    value: string | number;
  }[];
  social?: {
    website?: string;
    instagram?: string;
    twitter?: string;
    behance?: string;
    linkedin?: string;
  };
}
export interface ProfileTab {
  id: string;
  label: string;
  icon: any;
  count?: number;
}
interface ProfileLayoutProps {
  profile: ProfileData;
  tabs: ProfileTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
  actionButtons?: React.ReactNode;
  similarProfiles?: React.ReactNode;
}
// --- Components ---
export const QueueIndicator = ({
  status,
  slots,
  maxSlots,
  wait
}: {
  status: 'open' | 'waitlist' | 'closed';
  slots: number;
  maxSlots: number;
  wait: string;
}) => {
  const dk = useDarkClasses();
  const getStatusColor = () => {
    switch (status) {
      case 'open':
        return 'bg-green-500';
      case 'waitlist':
        return 'bg-amber-500';
      case 'closed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getStatusText = () => {
    switch (status) {
      case 'open':
        return 'Open for Commissions';
      case 'waitlist':
        return 'Waitlist Only';
      case 'closed':
        return 'Closed';
      default:
        return 'Unknown';
    }
  };
  const percentage = Math.round((maxSlots - slots) / maxSlots * 100);
  return <div className={`mt-6 p-4 rounded-xl border ${dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/80 border-gray-200'} backdrop-blur-sm`} data-id="element-3050" data-mp="2uzgsyzq5eeve">
      <div className="flex items-center justify-between mb-3" data-id="element-3051" data-mp="2g7zjgerpgnxk">
        <div className="flex items-center gap-2" data-id="element-3052" data-mp="2l9x6zl7524wb">
          <span className={`relative flex h-3 w-3`} data-id="element-3053" data-mp="38yq61qj24sv5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${getStatusColor()}`} data-id="element-3054" data-mp="38z9dueezu0vg"></span>
            <span className={`relative inline-flex rounded-full h-3 w-3 ${getStatusColor()}`} data-id="element-3055" data-mp="2zybggez28rlu"></span>
          </span>
          <span className={`font-bold text-sm ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3056" data-mp="33y278iol77qd">
            {getStatusText()}
          </span>
        </div>
        <span className={`text-xs font-medium ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3057" data-mp="1slj70zwhoyi4">
          ~{wait} wait
        </span>
      </div>

      {status !== 'closed' && <div className="space-y-2" data-id="element-3058" data-mp="1g0uejk5d93db">
          <div className="flex justify-between text-xs" data-id="element-3059" data-mp="1g3ypr1h90zdh">
            <span className={dk.isDark ? 'text-gray-400' : 'text-gray-600'} data-id="element-3060" data-mp="1uy3j5fjok3mz">
              {slots} of {maxSlots} slots available
            </span>
            <span className={dk.isDark ? 'text-gray-500' : 'text-gray-400'} data-id="element-3061" data-mp="1py7erf72ma6w">
              {percentage}% full
            </span>
          </div>
          <div className={`h-2 w-full rounded-full ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`} data-id="element-3062" data-mp="xc5xj9a07i6t">
            <div className={`h-full rounded-full transition-all duration-500 ${getStatusColor()}`} style={{
          width: `${percentage}%`
        }} data-id="element-3063" data-mp="1c1lkee6ez3h8" />
          </div>
        </div>}
    </div>;
};
const ProfileLayout: React.FC<ProfileLayoutProps> = ({
  profile,
  tabs,
  activeTab,
  onTabChange,
  children,
  actionButtons,
  similarProfiles
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-3064" data-mp="3mz2b40hqoviq">
      {/* Immersive Hero Section */}
      <div className="relative overflow-hidden" data-id="element-3065" data-mp="3rqamv36jksz2">
        {/* Background Image with Blur */}
        <div className="absolute inset-0 z-0" data-id="element-3066" data-mp="jzqvlge2iel">
          <div className={`absolute inset-0 ${dk.isDark ? 'bg-gray-900/80' : 'bg-white/80'} z-10`} data-id="element-3067" data-mp="j7s61rhw4k3" />
          <div className={`absolute inset-0 bg-gradient-to-b ${dk.isDark ? 'from-purple-900/40 to-gray-900' : 'from-purple-100/50 to-gray-50'} z-10`} data-id="element-3068" data-mp="5h9dktce8fyq" />
          <img src={profile.coverImage} alt="" className="w-full h-full object-cover filter blur-xl opacity-60 scale-110" data-id="element-3069" data-mp="eih9j4rft4pn" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-3070" data-mp="2gac7khkrtape">
          <div className="mb-8" data-id="element-3071" data-mp="28rw5bqf4y3j0">
            <BackButton className={dk.isDark ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-purple-600'} data-id="element-3072" data-mp="28t78bo0rb792" />
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-3073" data-mp="1u2q9mcfiavfv">
            {/* Avatar with Gradient Ring */}
            <div className="flex-shrink-0 relative w-full md:w-auto flex flex-col items-center md:items-start" data-id="element-3074" data-mp="2rr0csur13p8k">
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full p-1 bg-gradient-to-br from-purple-500 to-pink-500 shadow-2xl relative" data-id="element-3075" data-mp="2mr49kxq5o1u2">
                <img src={profile.avatar} alt={profile.name} className={`w-full h-full rounded-full object-cover border-4 ${dk.isDark ? 'border-gray-900' : 'border-white'}`} data-id="element-3076" data-mp="2hqhnvlklxijs" />
                <div className={`absolute bottom-2 right-2 px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1 ${dk.isDark ? 'bg-green-900/90 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200'}`} data-id="element-3077" data-mp="12b0wmtozszjf">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-3078" data-mp="tv1nk3bccqin" />
                  {profile.status}
                </div>
              </div>

              {/* Queue Indicator Component */}
              <div className="w-full max-w-xs" data-id="element-3079" data-mp="l1ya3v0p4do0">
                <QueueIndicator status={profile.queueStatus} slots={profile.slotsAvailable} maxSlots={profile.maxSlots} wait={profile.estimatedWait} data-id="element-3080" data-mp="l055el9n83in" />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 w-full" data-id="element-3081" data-mp="g10spknifnnk">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-3082" data-mp="24f0ap6eyr0j">
                <div data-id="element-3083" data-mp="63w65jpr5q68">
                  <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2 text-center md:text-left" data-id="element-3084" data-mp="64fsdcmfr0q2">
                    {profile.name}
                  </h1>
                  <p className={`text-lg font-medium mb-3 text-center md:text-left ${dk.isDark ? 'text-purple-300' : 'text-purple-600'}`} data-id="element-3085" data-mp="3enq6jmdg59vu">
                    {profile.role}
                  </p>

                  <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 text-sm mb-4" data-id="element-3086" data-mp="3nmbkt5bvy2qt">
                    <div className="flex items-center gap-1.5" data-id="element-3087" data-mp="6d6fiuqfph61">
                      <MapPinIcon className={`w-4 h-4 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3088" data-mp="1fn2fy5h3afo" />
                      <span className={dk.isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-3089" data-mp="b6hop2ipya14">
                        {profile.location}
                      </span>
                    </div>
                    {profile.specialties.map(spec => <span key={spec} className={`px-2 py-0.5 ${rt.badgeRadius} text-xs border ${dk.isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-600'}`} data-id="element-3090" data-mp="1rilip8cj3n05">
                        {spec}
                      </span>)}
                  </div>
                </div>

                <div className="flex gap-3 justify-center md:justify-start" data-id="element-3091" data-mp="17wp3sy5st37d">
                  {actionButtons || <>
                      <button className="px-6 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors shadow-lg shadow-purple-900/20 flex items-center gap-2" data-id="element-3092" data-mp="1cs4jzwra3b6y">
                        <MailIcon className="w-4 h-4" data-id="element-3093" data-mp="p4wcr9aahs2a" />
                        Hire Me
                      </button>
                      <button className={`px-6 py-2.5 rounded-lg text-sm font-bold border transition-colors flex items-center gap-2 ${dk.isDark ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-3094" data-mp="u3fz3assp5v6">
                        Follow
                      </button>
                    </>}
                </div>
              </div>

              {/* Bio */}
              <div className="mb-6 max-w-3xl mx-auto md:mx-0 text-center md:text-left" data-id="element-3095" data-mp="7mo2ijn9f20s">
                <p className={`leading-relaxed ${dk.isDark ? 'text-gray-300' : 'text-gray-600'} ${!isBioExpanded ? 'line-clamp-2' : ''}`} data-id="element-3096" data-mp="3mdz9equqppm">
                  {profile.bio}
                </p>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="text-purple-500 hover:text-purple-400 text-sm font-medium mt-1 inline-flex items-center" data-id="element-3097" data-mp="3q1gmm2ni1ixz">
                  {isBioExpanded ? 'Read Less' : 'Read More'}
                  <ChevronRightIcon className={`w-3 h-3 ml-0.5 transition-transform ${isBioExpanded ? 'rotate-90' : ''}`} data-id="element-3098" data-mp="3c27fdsh9tz65" />
                </button>
              </div>

              {/* Stats Row */}
              <div className={`flex flex-wrap justify-center md:justify-start gap-6 sm:gap-12 pt-6 border-t ${dk.isDark ? 'border-gray-700/50' : 'border-gray-200/50'}`} data-id="element-3099" data-mp="3k065mo3fdsle">
                {profile.stats.map((stat, idx) => <div key={idx} className="flex flex-col items-center md:items-start" data-id="element-3100" data-mp="2rc0la2gaa5m3" data-mpi={idx}>
                    <div className="flex items-center gap-1 mb-1" data-id="element-3101" data-mp="30bx7lxf7asdz">
                      <span className="text-2xl font-bold" data-id="element-3102" data-mp="30a2kqdkdcq79">{stat.value}</span>
                      {stat.label === 'Rating' && <StarIcon className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3103" data-mp="floa21h0we67" />}
                    </div>
                    <span className={`text-xs uppercase tracking-wider font-medium ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3104" data-mp="bjkv32v5arg6">
                      {stat.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Tab Bar */}
      <div className={`sticky top-0 z-30 border-b backdrop-blur-md ${dk.isDark ? 'bg-gray-900/90 border-gray-800' : 'bg-white/90 border-gray-200'}`} data-id="element-3105" data-mp="e3zffcf3c5co">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3106" data-mp="16tgbql5u06e6">
          <div className="flex overflow-x-auto scrollbar-hide" data-id="element-3107" data-mp="16sog5ac8i8nw">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => onTabChange(tab.id)} className={`relative flex items-center gap-2 py-4 px-6 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-purple-600' : dk.isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3108" data-mp="x1k1kbqh0ptl">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-purple-600' : ''}`} data-id="element-3109" data-mp="1uhcvw29oflw6" />
                  {tab.label}
                  {tab.count !== undefined && <span className={`ml-1.5 text-xs py-0.5 px-2 rounded-full ${isActive ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : dk.isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-50'}`} data-id="element-3110" data-mp="1lfvjouze44cr">
                      {tab.count}
                    </span>}
                  {isActive && <motion.div layoutId="activeProfileTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" data-id="element-3111" data-mp="12ri7q8xk2293" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-id="element-3112" data-mp="34h7cnsvhamca">
        <AnimatePresence mode="wait" data-id="element-3113" data-mp="39c34arwsz7ov">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-3114" data-mp="3edabopv1a4v0">
            {children}
          </motion.div>
        </AnimatePresence>

        {/* Similar Profiles */}
        {similarProfiles && <div className={`mt-20 pt-10 border-t ${dk.isDark ? 'border-gray-800' : 'border-gray-200'}`} data-id="element-3115" data-mp="2zfzztfehawyr">
            {similarProfiles}
          </div>}
      </div>
    </div>;
};
export default ProfileLayout;